import { atom, SetterOrUpdater } from 'recoil';

export interface CountryData {
  name: string;
  code: string;
  currency: string;
  currency_symbol: string;
  phoneCode: string;
  countryId: number;
  timezone?: string;
}

export interface GlobalCountry {
  countryCode: string | null;
  phoneCode: string | null;
  countryId: number | null;
  name: string | null;
  currency: string | null;
  currency_symbol: string | null;
  timezone: string | null;
}

let globalCountry: GlobalCountry = {
  countryCode: null,
  phoneCode: null,
  countryId: null,
  name: null,
  currency: null,
  currency_symbol: null,
  timezone: null,
};

export interface CountryState {
  isOpen: boolean;
  selectedCountry: CountryData | null;
  source?: 'manual' | 'auto';
  globalCountryChange?: boolean;
}

export const countryAtom = atom<CountryState>({
  key: 'countryAtom',
  default: {
    isOpen: false,
    selectedCountry: null,
    source: undefined,
    globalCountryChange: false,
  },
});

export const setGlobalCountry = (
  countryCode: string | null,
  phoneCode: string | null = null,
  countryId: number | null = null,
  name: string | null = null,
  currency: string | null = null,
  currency_symbol: string | null = null,
  timezone: string | null = null,
): void => {
  globalCountry = {
    countryCode,
    phoneCode,
    countryId,
    name,
    currency,
    currency_symbol,
    timezone,
  };
};

export const getGlobalCountry = (
  setCountryState?: SetterOrUpdater<CountryState>,
): GlobalCountry => {
  try {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const cnParam = urlParams.get('cn')?.toUpperCase();
      if (cnParam) {
        const rawActive = localStorage.getItem('/api/v4/active-countries');
        if (rawActive) {
          try {
            const parsed = JSON.parse(rawActive);
            const list = parsed?.data || [];
            const countryObj = list.find(
              (c: any) => c.code?.toUpperCase() === cnParam,
            );

            if (countryObj) {
              let timezone = 'Asia/Kolkata';
              if (countryObj.timezones?.[0]) {
                try {
                  const parsedTz =
                    typeof countryObj.timezones[0] === 'string'
                      ? JSON.parse(countryObj.timezones[0])
                      : countryObj.timezones[0];
                  timezone =
                    parsedTz?.[0]?.zoneName ||
                    parsedTz?.zoneName ||
                    'Asia/Kolkata';
                } catch {
                  timezone = 'Asia/Kolkata';
                }
              }

              const rawPhone = countryObj.phoneCode || countryObj.phonecode;
              const phoneCode = rawPhone
                ? rawPhone.toString().startsWith('+')
                  ? rawPhone.toString()
                  : `+${rawPhone}`
                : '+91';

              const countryPayload = {
                ...countryObj,
                phoneCode,
                timezone,
              };

              globalCountry = {
                countryCode: countryObj.code || null,
                phoneCode,
                countryId: countryObj.id || countryObj.countryId || null,
                name: countryObj.name || null,
                currency: countryObj.currency || null,
                currency_symbol: countryObj.currency_symbol || null,
                timezone,
              };

              localStorage.setItem(
                'zebra_selected_country',
                JSON.stringify({
                  value: {
                    country: countryPayload,
                    source: 'manual',
                  },
                  expiry: new Date().getTime() + 24 * 60 * 60 * 1000,
                }),
              );

              if (countryObj.code) {
                document.cookie = `country=${countryObj.code}; path=/; max-age=${60 * 60 * 24}; SameSite=Lax`;
              }

              if (setCountryState) {
                setCountryState({
                  isOpen: false,
                  selectedCountry: {
                    name: countryObj.name,
                    code: countryObj.code,
                    currency: countryObj.currency,
                    currency_symbol: countryObj.currency_symbol,
                    phoneCode,
                    countryId: countryObj.id || countryObj.countryId,
                    timezone,
                  },
                  source: 'manual',
                  globalCountryChange: false,
                });
              }

              return globalCountry;
            }
          } catch (error) {
            console.warn(
              'Failed to parse active countries from localStorage:',
              error,
            );
          }
        }
      }

      const stored = localStorage.getItem('zebra_selected_country');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const data = parsed?.value?.country || parsed?.country || parsed;
          if (data && data.code) {
            globalCountry = {
              countryCode: data.code || null,
              phoneCode: data.phoneCode || null,
              countryId: data.countryId || null,
              name: data.name || null,
              currency: data.currency || null,
              currency_symbol: data.currency_symbol || null,
              timezone: data.timezone || null,
            };

            if (setCountryState) {
              setCountryState({
                isOpen: false,
                selectedCountry: {
                  name: data.name,
                  code: data.code,
                  currency: data.currency,
                  currency_symbol: data.currency_symbol,
                  phoneCode: data.phoneCode,
                  countryId: data.countryId,
                  timezone: data.timezone,
                },
                source: 'auto',
                globalCountryChange: false,
              });
            }

            return globalCountry;
          }
        } catch {
          localStorage.removeItem('zebra_selected_country');
        }
      }

      const match = document.cookie.match(/country=([A-Z]{2})/i);
      if (match && match[1]) {
        globalCountry.countryCode = match[1].toUpperCase();

        if (setCountryState) {
          setCountryState((prev) => ({
            ...prev,
            selectedCountry: {
              name: globalCountry.name || 'India',
              code: globalCountry.countryCode || 'IN',
              currency: globalCountry.currency || 'INR',
              currency_symbol: globalCountry.currency_symbol || '₹',
              phoneCode: globalCountry.phoneCode || '+91',
              countryId: globalCountry.countryId || 101,
              timezone: globalCountry.timezone || 'Asia/Kolkata',
            },
          }));
        }

        return globalCountry;
      }
    }
  } catch (err) {
    console.warn('getGlobalCountry failed:', err);
  }

  if (!globalCountry.countryCode) {
    globalCountry = {
      countryCode: 'IN',
      phoneCode: '+91',
      countryId: 101,
      name: 'India',
      currency: 'INR',
      currency_symbol: '₹',
      timezone: 'Asia/Kolkata',
    };

    if (setCountryState) {
      setCountryState({
        isOpen: false,
        selectedCountry: {
          name: 'India',
          code: 'IN',
          currency: 'INR',
          currency_symbol: '₹',
          phoneCode: '+91',
          countryId: 101,
          timezone: 'Asia/Kolkata',
        },
        source: 'auto',
        globalCountryChange: false,
      });
    }
  }

  return globalCountry;
};

export const isSubscriptionDisabled = (): boolean => {
  const countryCode = getGlobalCountry().countryCode?.toUpperCase() || 'IN';
  return countryCode !== 'IN';
};
